/** While the usage guide is on screen, real shortcuts open real surfaces but
 *  nothing they produce is sent; the guide shows why. */
import { useEffect, useState } from "react";
import { translate } from "@/i18n";
import { onboardingGuideMessages } from "@/i18n/messages/onboarding-guide";
import { useOnboardingStore } from "@/stores/onboarding-store";
import type { OnboardingStep } from "@/api/onboarding";
import { contextualDiscoveryPresentationHeld } from "../contextual-discovery-presentation";
import type { ContextualStoryState } from "../contextual-story-state";

/** Why a send is held while the guide is on screen, in the app language. */
export function guideSendHoldHint(): string {
  return translate(onboardingGuideMessages, "sendHoldHint");
}
const GUIDE_SEND_HOLD_HINT_MS = 4_000;
const VOICE_LESSON_FINAL_GRACE_MS = 5_000;

export type GuideHoldState = {
  restarting: boolean;
  step: OnboardingStep | null;
  contextualStory: ContextualStoryState | null;
};

/** The guide is on screen: the basics are presented, or the legacy guide phase. */
export function guideHoldsSends(state: GuideHoldState): boolean {
  const story = state.contextualStory;
  return (
    !state.restarting &&
    state.step === "desktop_usage_primer" &&
    !!story &&
    (contextualDiscoveryPresentationHeld(story) || story.phase === "guide")
  );
}

export function useGuideHoldsSends(): boolean {
  return useOnboardingStore(guideHoldsSends);
}

/** The voice lesson's own words: spoken while the mic is open, or the final
 *  that arrives as it closes. Holding them is expected, so no hint. */
function voiceLessonSpeech(story: ContextualStoryState): boolean {
  return (
    !!story.guideOpenSlides?.includes("voice") ||
    (story.guideVoiceClosedAt !== undefined &&
      Date.now() - story.guideVoiceClosedAt < VOICE_LESSON_FINAL_GRACE_MS)
  );
}

/** True when the send must wait; the guide then shows the hint on every display. */
export function holdSendForGuide(source: "typed" | "voice"): boolean {
  const state = useOnboardingStore.getState();
  if (!guideHoldsSends(state)) return false;
  if (source === "voice" && voiceLessonSpeech(state.contextualStory!))
    return true;
  useOnboardingStore.setState({
    contextualStory: { ...state.contextualStory!, guideHoldHintAt: Date.now() },
  });
  return true;
}

/** Shows the hint for a few seconds after each held send attempt, and only
 *  while the guide still holds sends. */
export function useGuideSendHoldHintVisible(): boolean {
  const holds = useGuideHoldsSends();
  const heldAt = useOnboardingStore(
    (state) => state.contextualStory?.guideHoldHintAt,
  );
  const [visibleAt, setVisibleAt] = useState<number | null>(null);
  const [seenAt, setSeenAt] = useState(heldAt);
  if (heldAt !== seenAt) {
    // A new attempt shows at once; waiting for the effect would flicker.
    setSeenAt(heldAt);
    setVisibleAt(heldAt ?? null);
  }
  useEffect(() => {
    if (!heldAt) return;
    const remaining = heldAt + GUIDE_SEND_HOLD_HINT_MS - Date.now();
    if (remaining <= 0) return;
    setVisibleAt(heldAt);
    const timer = setTimeout(() => setVisibleAt(null), remaining);
    return () => clearTimeout(timer);
  }, [heldAt]);
  return holds && visibleAt !== null && visibleAt === heldAt;
}
